import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Utensils, Loader2, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useCart } from "@/lib/cart-context";
import { useCountry } from "@/lib/country-context";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { OrderConfirmCard } from "@/components/order-confirm-card";
import { LocationPromptCard } from "@/components/location-prompt-card";
import { LocationPicker } from "@/components/location-picker";
import api from "@/lib/api";

const SUGGESTIONS = [
  { label: "Jollof rice", prompt: "I want jollof rice" },
  { label: "Something spicy", prompt: "I want something really spicy" },
  { label: "Late night suya", prompt: "I want suya for late night" },
  { label: "Light soup & fufu", prompt: "I want fufu with light soup" },
  { label: "Quick snack", prompt: "I want a quick snack" },
];

export default function ChatPage() {
  const { isCustomer, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { formatPrice } = useCountry();
  const { toast } = useToast();

  const [sessionId, setSessionId] = useState(() => localStorage.getItem("cravve-session"));
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [activeDraft, setActiveDraft] = useState(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [pickedCoords, setPickedCoords] = useState(null);
  const scrollRef = useRef(null);

  // Load history when session exists
  useEffect(() => {
    if (!sessionId || !isCustomer) return;
    api
      .get("/chat/history", { params: { session_id: sessionId } })
      .then(({ data }) =>
        setMessages(
          data.map((m) => ({
            role: m.role,
            content: m.content,
            suggestions: m.suggestions || [],
          }))
        )
      )
      .catch(() => {
        // ignore
      });
  }, [sessionId, isCustomer]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const newChat = () => {
    localStorage.removeItem("cravve-session");
    setSessionId(null);
    setMessages([]);
    setActiveDraft(null);
  };

  const sendMessage = async (text, { startOrderItemId, deliveryLatitude, deliveryLongitude } = {}) => {
    if (!text.trim() || sending) return;
    if (!isCustomer) {
      navigate("/signin");
      return;
    }
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", {
        message: text,
        session_id: sessionId,
        start_order_item_id: startOrderItemId,
        delivery_latitude: deliveryLatitude,
        delivery_longitude: deliveryLongitude,
      });
      if (!sessionId) {
        localStorage.setItem("cravve-session", data.session_id);
        setSessionId(data.session_id);
      }
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.message, suggestions: data.suggestions || [] },
      ]);
      setActiveDraft(data.order_draft || null);
    } catch (e) {
      toast({ title: "Failed to send message", variant: "destructive" });
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const text = input;
    setInput("");
    await sendMessage(text);
  };

  const addSuggestion = (s) => {
    sendMessage(`I'd like to order ${s.name}.`, { startOrderItemId: s.menu_item_id });
  };

  const onOrderPlaced = (order) => {
    // Leave activeDraft as-is (still "ready") so the card stays mounted and
    // shows its own success view — it gets replaced next time a new draft
    // comes back from the chat API (new order, or none).
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content: `Done! Your order is confirmed. Order ID #${order.id.slice(0, 8).toUpperCase()} — I'll keep an eye on it for you.`,
      },
    ]);
    toast({ title: "Order placed!", description: `Tap below to track #${order.id.slice(0, 8).toUpperCase()}` });
  };

  const onOrderCancel = () => {
    sendMessage("Actually, cancel that order.");
  };

  const useCurrentLocationForOrder = () => {
    if (!("geolocation" in navigator)) {
      toast({ title: "Location isn't supported by your browser", variant: "destructive" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        sendMessage("Use my current location.", {
          deliveryLatitude: pos.coords.latitude,
          deliveryLongitude: pos.coords.longitude,
        });
      },
      () => {
        toast({
          title: "Couldn't get your location",
          description: "Check your browser's location permission, or just type your address.",
          variant: "destructive",
        });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const openMapPicker = () => {
    setPickedCoords(null);
    setMapPickerOpen(true);
  };

  const confirmPickedLocation = () => {
    if (!pickedCoords) return;
    sendMessage("I picked a location on the map.", {
      deliveryLatitude: pickedCoords.latitude,
      deliveryLongitude: pickedCoords.longitude,
    });
    setMapPickerOpen(false);
  };

  // Show the "use my location" quick action exactly while the AI is waiting on
  // the delivery address (quantity already set, address not yet provided).
  const awaitingAddress =
    activeDraft &&
    activeDraft.status === "collecting" &&
    activeDraft.quantity &&
    !activeDraft.delivery_address;

  if (authLoading) {
    return (
      <div className="flex h-full items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isCustomer) {
    return (
      <div className="flex flex-col h-[calc(100vh-65px)] md:h-screen items-center justify-center bg-background px-6">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <Utensils className="w-10 h-10 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold">Welcome to Cravve</h1>
            <p className="text-muted-foreground mt-2">
              Sign in to chat with your AI food assistant and discover the best West African dishes.
            </p>
          </div>
          <Button
            className="w-full h-12 text-base font-semibold"
            onClick={() => navigate("/signin")}
            data-testid="chat-signin-cta"
          >
            Sign in to start chatting
          </Button>
          <p className="text-xs text-muted-foreground">
            New here?{" "}
            <button onClick={() => navigate("/signup")} className="text-primary underline">
              Create an account
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-65px)] md:h-screen bg-background">
      {messages.length > 0 && (
        <div className="flex items-center justify-end px-4 py-2 border-b bg-background/80 backdrop-blur">
          <Button
            variant="ghost" size="sm"
            className="text-muted-foreground hover:text-foreground gap-1.5 text-xs"
            onClick={newChat}
            data-testid="chat-new-btn"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            New chat
          </Button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto space-y-6 mt-10">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-2">
              <Utensils className="w-10 h-10 text-primary" />
            </div>
            <h1 className="font-serif text-4xl font-bold text-foreground">What are you craving?</h1>
            <p className="text-muted-foreground text-lg">
              From jollof rice to suya, fufu to kelewele — tell me what you&apos;re in the mood for.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              {SUGGESTIONS.map(({ label, prompt }) => (
                <button
                  key={label}
                  onClick={() => setInput(prompt)}
                  data-testid={`chat-quick-${label.replace(/\s+/g, "-").toLowerCase()}`}
                  className="px-4 py-2 bg-card border rounded-full text-sm font-medium hover:border-primary hover:text-primary transition-colors shadow-sm"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex flex-col max-w-2xl ${msg.role === "user" ? "ml-auto" : "mr-auto"}`}>
              <div
                data-testid={`chat-msg-${msg.role}-${i}`}
                className={`p-4 rounded-2xl shadow-sm whitespace-pre-wrap ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-tr-sm"
                    : "bg-card border rounded-tl-sm text-card-foreground"
                }`}
              >
                {msg.content}
              </div>
              {msg.suggestions && msg.suggestions.length > 0 && (
                <div className="mt-4 flex flex-col gap-3">
                  {msg.suggestions.map((s, idx) => (
                    <div
                      key={`${i}-${idx}`}
                      className="flex items-start gap-4 p-4 bg-card border rounded-xl hover:shadow-sm transition-shadow"
                      data-testid={`chat-suggestion-${s.menu_item_id}`}
                    >
                      {s.image_url ? (
                        <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-muted">
                          <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <Utensils className="w-8 h-8 text-muted-foreground/30" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold truncate">{s.name}</h4>
                        <p className="text-xs text-muted-foreground truncate">{s.restaurant_name}</p>
                        <p className="text-xs mt-1 text-muted-foreground/80 italic">&ldquo;{s.reason}&rdquo;</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-medium text-primary">{formatPrice(s.price)}</span>
                          <Button
                            size="sm" className="h-7 text-xs bg-gradient-primary text-primary-foreground shadow-glow"
                            onClick={() => addSuggestion(s)}
                            data-testid={`chat-add-${s.menu_item_id}`}
                          >
                            Order
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}

        {awaitingAddress && (
          <div className="flex max-w-2xl mr-auto">
            <LocationPromptCard
              onUseCurrentLocation={useCurrentLocationForOrder}
              onPickOnMap={openMapPicker}
              busy={sending}
            />
          </div>
        )}

        {sending && (
          <div className="flex max-w-2xl mr-auto">
            <div className="p-4 rounded-2xl bg-card border rounded-tl-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Thinking…</span>
            </div>
          </div>
        )}

        {activeDraft && activeDraft.status === "ready" && (
          <div className="flex max-w-2xl mr-auto">
            <OrderConfirmCard
              draft={activeDraft}
              sessionId={sessionId}
              onPlaced={onOrderPlaced}
              onCancel={onOrderCancel}
              busy={sending}
            />
          </div>
        )}
      </div>

      <div className="p-4 bg-background border-t">
        <form onSubmit={submit} className="max-w-3xl mx-auto relative flex items-center">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Craving something specific?"
            className="w-full h-14 pl-6 pr-14 rounded-full border-input bg-card shadow-sm text-base focus-visible:ring-primary/50"
            data-testid="chat-input"
          />
          <Button
            type="submit" size="icon" className="absolute right-2 h-10 w-10 rounded-full"
            disabled={!input.trim() || sending}
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>

      <Dialog open={mapPickerOpen} onOpenChange={setMapPickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pick your delivery location</DialogTitle>
          </DialogHeader>
          <LocationPicker value={pickedCoords} onChange={setPickedCoords} />
          <DialogFooter>
            <Button
              type="button"
              className="w-full"
              disabled={!pickedCoords}
              onClick={confirmPickedLocation}
              data-testid="chat-map-picker-confirm"
            >
              Use this location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
